// YOUR ROOM, HOUR BY HOUR (Oct 2026), shared by the AC guides.
//
// Saheb's point (17 Oct 2026): an inverter only saves when it has spare
// capacity. If a 1.5 ton AC cannot bring a room down to its setting, both an
// inverter and a non-inverter run at full power all the time, and the inverter
// saves nothing. Whether that happens depends on the city, the floor, the
// room, the sun, the setting and the hours. So this works it out hour by hour
// for one room on one day, with the AC calculator's own engine.
//
// HOW
//  1. Outdoor temperature through the day: from the city's IMD figures in
//     data/indiaClimate.js. A typical hot-month day runs from the mean daily
//     minimum to the mean daily maximum; the hottest day of an average year
//     peaks at `peak` with the same daily range. The curve is smooth, lowest at
//     6 am and highest at 3 pm.
//  2. The room's load each hour: Daikin's heat-load sheet (lib/heatLoad.js),
//     the same factors and margins, with the sun-driven parts scaled by hour:
//     glass by the sun on that window, walls and roof by how much stored heat
//     they carry (highest at 4 pm, still well up in the evening, lowest at
//     dawn). At 3 to 4 pm on the hottest day it lands on the calculator's
//     number.
//  3. What each AC gives that hour: rated kW up to its series' full-capacity
//     temperature, then 5% less per °C (lib/heatLoad.js capacityTR, the
//     calculator's rule).
//  4. If the room needs more than the AC gives, the AC runs at full power and the
//     room settles above the setting, where the load equals what the AC gives.
//     Inverter or not, it draws its full power: 230 V × Daikin's rated
//     operating current (data/acSpecs.js).
//  5. If the AC has spare capacity: a non-inverter runs at full power for the
//     share of the hour it is needed and switches off for the rest. An
//     inverter turns down. Turned well down (60% or less of its power) we take
//     its efficiency as its ISEER, the BEE figure for its usual mix of hours;
//     from there it falls in a straight line to its full-power efficiency.
//     Below Daikin's minimum capacity it cycles at that minimum.
//
// Every AC gets the same treatment for things this leaves out (efficiency
// falling in extreme heat, a non-inverter's restart losses), so the
// comparison between them stays fair.
//
// No imports: scripts/prerender.js loads this file by stripping `export`.
// The caller passes the heat-load module (HL), a station from STATIONS, and
// the ACs with their specs.

export const DAY_TYPES = [
  { key: 'typical', label: 'Typical hot-month day' },
  { key: 'hottest', label: 'Hottest day of the year' },
];

export const SCHEDULES = [
  { key: 'night', label: 'Night', note: '10 pm–6 am', hours: [22, 23, 0, 1, 2, 3, 4, 5] },
  { key: 'evening', label: 'Evening', note: '6 pm–2 am', hours: [18, 19, 20, 21, 22, 23, 0, 1] },
  { key: 'afternoon', label: 'Afternoon', note: '12–6 pm', hours: [12, 13, 14, 15, 16, 17] },
  { key: 'office', label: 'Office hours', note: '9 am–6 pm', hours: [9, 10, 11, 12, 13, 14, 15, 16, 17] },
  { key: 'allday', label: 'All day', note: '24 hours', hours: Array.from({ length: 24 }, (_, i) => i) },
];

export const ROOM_SIZES = [
  { key: '10x12', l: 12, w: 10 },
  { key: '12x12', l: 12, w: 12 },
  { key: '12x15', l: 15, w: 12 },
  { key: '14x16', l: 16, w: 14 },
];

export const FLOORS = [
  { key: 'top', label: 'Top floor' },
  { key: 'mid', label: 'Middle floor' },
  { key: 'ground', label: 'Ground floor' },
];

export const SUNS = [
  { key: 'W', label: 'West' }, { key: 'S', label: 'South' }, { key: 'E', label: 'East' }, { key: 'N', label: 'North' },
];

export const SETTINGS = [22, 24, 26];

export const ROOM_KINDS = [
  { key: 'bedroom', label: 'Bedroom' },
  { key: 'living', label: 'Living room' },
];

export const ROOM_DEFAULTS = {
  city: 'delhi', kind: 'bedroom', size: '12x12', floor: 'mid', sun: 'W', setC: 24, day: 'typical', schedule: 'night',
};

// Two rooms the guides open with: one an inverter suits, one it doesn't.
export const ROOM_PRESETS = [
  { key: 'easy', label: 'Middle-floor bedroom, at night', room: { ...ROOM_DEFAULTS } },
  { key: 'hard', label: 'Top-floor room, afternoons, hottest day', room: { ...ROOM_DEFAULTS, floor: 'top', schedule: 'afternoon', day: 'hottest' } },
];

const TURNED_DOWN = 0.6;   // at or below this share of full power, efficiency = ISEER

// ─── the day ──────────────────────────────────────────────────────────────

// 0 at 6 am, 1 at 3 pm, smooth both ways.
export function tempShape(h) {
  if (h >= 6 && h <= 15) return 0.5 - 0.5 * Math.cos((Math.PI * (h - 6)) / 9);
  const d = (h - 15 + 24) % 24;
  return 0.5 + 0.5 * Math.cos((Math.PI * d) / 15);
}

// Sun on a window, as a share of its strongest hour. West-facing glass peaks
// at 4 pm (the sheet's hour); east-facing glass is its mirror in the morning.
const WEST_SUN = { 12: 0.15, 13: 0.35, 14: 0.6, 15: 0.85, 16: 1, 17: 0.9, 18: 0.5, 19: 0.1 };
const EAST_SUN = { 6: 0.1, 7: 0.5, 8: 0.9, 9: 1, 10: 0.85, 11: 0.6, 12: 0.35, 13: 0.15 };
const DIFFUSE = 0.08;
// Stored heat in walls and roof, as a share of 4 pm.
const MASS = [0.55, 0.5, 0.45, 0.42, 0.4, 0.38, 0.36, 0.38, 0.45, 0.55, 0.65, 0.74,
  0.82, 0.89, 0.95, 0.99, 1, 0.98, 0.92, 0.84, 0.76, 0.69, 0.63, 0.59];
const MIRROR = { E: 'W', NE: 'NW', SE: 'SW' };

function glassGain(HL, dir, area, shgc, h) {
  const day = h >= 7 && h <= 18;
  if (dir === 'W' || dir === 'SW' || dir === 'NW') {
    const s = WEST_SUN[h] != null ? WEST_SUN[h] : (day ? DIFFUSE : 0);
    return area * HL.SHGF.summer[dir] * shgc * s;
  }
  if (MIRROR[dir]) {
    const s = EAST_SUN[h] != null ? EAST_SUN[h] : (day ? DIFFUSE : 0);
    return area * HL.SHGF.summer[MIRROR[dir]] * shgc * s;
  }
  return day ? area * (HL.SHGF.summer[dir] || 0) * shgc : 0;
}

// The sheet's summer sum at one hour, with outdoor dry bulb `outF` and the
// room held at `inside`. Same factors, margins and order as seasonLoad().
export function hourLoad(HL, room, outF, gr, inside, dailyRangeF, h) {
  const F = HL.FACTORS;
  const td = outF - inside.dbF;
  const dGr = gr - inside.gr;
  const corr = HL.etdCorrection(td, dailyRangeF);
  const tdAdj = td - 5;
  const g = HL.GLASS[room.glassType] || HL.GLASS.single;
  let glassSolar = 0; let glassArea = 0;
  Object.entries(room.glass || {}).forEach(([dir, a]) => {
    glassSolar += glassGain(HL, dir, a, g.shgc, h);
    glassArea += a;
  });
  let walls = 0;
  Object.entries(room.walls || {}).forEach(([dir, a]) => {
    if (HL.ETD_BASE[dir] != null) walls += a * Math.max(0, HL.ETD_BASE[dir] * MASS[h] + corr) * HL.U.wall;
  });
  const roof = (room.roof || 0) * Math.max(0, HL.ETD_BASE.ROOF * MASS[h] + corr) * (room.roofInsulated ? HL.U.roofInsulated : HL.U.roofBare);
  const floor = (room.floor || 0) * Math.max(0, tdAdj) * HL.U.floor;
  const ceiling = (room.ceiling || 0) * Math.max(0, tdAdj) * HL.U.ceiling;
  const glassCond = glassArea * td * g.u;
  const partition = (room.partition || 0) * Math.max(0, tdAdj) * HL.U.partition;
  const people = (room.people || 0) * F.peopleSensible;
  const equip = (room.equipKW || 0) * F.equipFactor * F.btuPerKW;
  const lights = (room.lightsW || 0) * F.lightsFactor * F.btuPerWatt;
  const subtotal = glassSolar + walls + roof + floor + ceiling + glassCond + partition + people + equip + lights;
  const sf = room.safetySensible != null ? room.safetySensible : F.safetySensible;
  const rsh = subtotal * (1 + sf / 100);
  const cfm = room.freshAirCfm != null ? room.freshAirCfm : (room.people || 0) * F.freshAirPerPerson + room.area * F.freshAirPerSqft;
  const bf = F.bypass;
  const ersh = rsh + (rsh * F.ductGain) / 100 + (rsh * F.fanGain) / 100 + cfm * bf * td * 1.08;
  const erlh = (room.people || 0) * F.peopleLatent * (1 + F.safetyLatent / 100) + cfm * bf * dGr * 0.68;
  const total = ersh + erlh + cfm * (1 - bf) * td * 1.08 + cfm * (1 - bf) * dGr * 0.68;
  return Math.max(0, total) / 12000 * HL.KW_PER_TR;   // kW
}

const insideC = (HL, c) => {
  const dbF = HL.cToF(c);
  return { dbF, gr: HL.grainsAt(dbF, 55) };
};

// The room's whole day: outdoor °C and the load (kW) held at the setting,
// for each hour 0..23.
export function dayLoads(HL, station, r) {
  const climate = HL.climateFor(station);
  const size = ROOM_SIZES.find((s) => s.key === r.size) || ROOM_SIZES[1];
  const room = HL.roomFor({ type: r.kind, l: size.l, w: size.w, floor: r.floor, sun: r.sun });
  const range = climate.dailyRangeC;
  const max = r.day === 'hottest' ? climate.peakC : station.hi;
  const min = max - range;
  const gr = climate.summer.gr;
  const set = HL.insideAt(r.setC);
  return Array.from({ length: 24 }, (_, h) => {
    const outC = min + (max - min) * tempShape(h);
    const outF = HL.cToF(outC);
    return { h, outC, needKw: hourLoad(HL, room, outF, gr, set, climate.dailyRangeF, h), room, outF, gr, dr: climate.dailyRangeF };
  });
}

// Where the room settles when the AC gives `capKw` and can't hold the setting.
function settleC(HL, hour, capKw, setC) {
  let lo = setC; let hi = setC + 20;
  for (let i = 0; i < 40; i += 1) {
    const mid = (lo + hi) / 2;
    const need = hourLoad(HL, hour.room, hour.outF, hour.gr, insideC(HL, mid), hour.dr, hour.h);
    if (need > capKw) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

// One AC at one moment: the room needs `needKw` with `outC` outside. Returns
// { state, share, capKw, kw, pFull } where state is 'flat' (can't keep up),
// 'cycle' (a non-inverter switching on and off), 'down' (an inverter turned
// down) or 'min' (an inverter at its lowest, still more than the room needs).
// `ac` = { model, full, spec } as in runAC.
export function acAtLoad(HL, ac, needKw, outC, volts = 230) {
  const ratedKw = ac.model.kw;
  const pFull = (ac.spec.amps * volts) / 1000;
  const capKw = HL.capacityTR(ratedKw, ac.full, outC) * HL.KW_PER_TR;
  if (needKw > capKw + 1e-9) return { state: 'flat', share: 1, capKw, kw: pFull, pFull };
  const share = capKw > 0 ? needKw / capKw : 0;
  if (!ac.model.inverter) return { state: 'cycle', share, capKw, kw: pFull * share, pFull };
  const eFull = ratedKw / pFull;
  let eff;
  if (share <= TURNED_DOWN) eff = ac.model.iseer;
  else eff = ac.model.iseer + ((eFull - ac.model.iseer) * (share - TURNED_DOWN)) / (1 - TURNED_DOWN);
  const state = needKw < (ac.spec.minKw || 0) ? 'min' : 'down';
  return { state, share, capKw, kw: needKw / eff, pFull };
}

// One AC through the day. `ac` = { model, full, spec } where full is its
// series' full-capacity temperature and spec its entry in AC_SPECS.
export function runAC(HL, day, ac, setC, volts = 230) {
  return day.map((hr) => {
    const x = acAtLoad(HL, ac, hr.needKw, hr.outC, volts);
    const roomC = x.state === 'flat' ? settleC(HL, hr, x.capKw, setC) : setC;
    return { h: hr.h, state: x.state, share: x.share, capKw: x.capKw, kw: x.kw, roomC };
  });
}

// One moment in one room: the hour `h` of room `r`'s day, with what each AC
// does then. needTR and capTR in ton.
export function momentCheck(HL, station, r, h, acs) {
  const day = dayLoads(HL, station, r);
  const d = day[h];
  const rows = acs.map((ac) => {
    const run = runAC(HL, day, ac, r.setC)[h];
    return { ac, ...run, capTR: run.capKw / HL.KW_PER_TR };
  });
  return { h, outC: d.outC, needKw: d.needKw, needTR: d.needKw / HL.KW_PER_TR, rows };
}

// The whole comparison for one room, for the hours it is used.
export function roomComparison(HL, station, r, acs) {
  const day = dayLoads(HL, station, r);
  const hours = (SCHEDULES.find((s) => s.key === r.schedule) || SCHEDULES[0]).hours;
  const used = new Set(hours);
  const rows = acs.map((ac) => {
    const run = runAC(HL, day, ac, r.setC);
    const inUse = run.filter((x) => used.has(x.h));
    const units = inUse.reduce((a, x) => a + x.kw, 0);
    const flatHours = inUse.filter((x) => x.state === 'flat').length;
    const warmest = Math.max(...inUse.map((x) => x.roomC));
    return { ac, run, units, flatHours, warmest, pFull: (ac.spec.amps * 230) / 1000 };
  });
  const peak = day.reduce((a, b) => (b.needKw > a.needKw ? b : a), day[0]);
  const peakUsed = day.filter((x) => used.has(x.h)).reduce((a, b) => (b.needKw > a.needKw ? b : a), day.find((x) => used.has(x.h)));
  return { day, hours, rows, peak, peakUsed };
}

// The ACs to compare hour by hour: one of each kind among the given size whose
// Daikin specs are in AC_SPECS. In stock first, then not sold out, then the
// cheapest, the same order as pickGuideModels() in data/guideInverter.js.
export function roomACs({ catalogue, prices, stock, series, specs, size = '1.5' }) {
  // Must agree with onlineKit() in lib/listing.js.
  const kitOf = (m) => {
    const v = m.variants;
    if (!v.withoutKit) return 'withKit';
    const s = (stock || {})[m.id] || {};
    return v.withKit && !(s.withoutKit > 0) && s.withKit > 0 ? 'withKit' : 'withoutKit';
  };
  const typeOf = (m) => (!m.inverter ? 'fixed' : m.series === 'FTHT' ? 'heat' : m.stars >= 5 ? 'five' : 'three');
  const pool = catalogue
    .filter((m) => m.soldAs === size && specs[m.id] && typeOf(m) !== 'heat')
    .map((m) => {
      const kit = kitOf(m);
      const left = (stock[m.id] || {})[kit];
      return {
        model: m,
        code: (String(m.variants[kit].idu).match(/^[A-Z]+\d+/) || [m.id])[0],
        type: typeOf(m),
        full: (series[m.series] && series[m.series].heat && series[m.series].heat.full) || 43,
        spec: specs[m.id],
        price: (prices[m.id] || {})[kit],
        inStock: (left || 0) > 0,
        soldOut: left === 0,
      };
    });
  const best = (type) => pool.filter((x) => x.type === type)
    .sort((a, b) => (Number(b.inStock) - Number(a.inStock)) || (Number(a.soldOut) - Number(b.soldOut))
      || ((a.price || 1e9) - (b.price || 1e9)))[0] || null;
  const out = { fixed: best('fixed'), three: best('three'), five: best('five') };
  out.list = [out.fixed, out.three, out.five].filter(Boolean);
  return out;
}

// The smallest bigger AC that holds the setting through every hour used.
// Capacity only (catalogue kW, series full-capacity temperature).
export function biggerAC(HL, cmp, catalogue, series, fromSize = '1.5') {
  const used = new Set(cmp.hours);
  const hours = cmp.day.filter((d) => used.has(d.h));
  const sizes = [...new Set(catalogue.map((m) => m.soldAs))]
    .filter((s) => Number(s) > Number(fromSize)).sort((a, b) => Number(a) - Number(b));
  for (const size of sizes) {
    const holds = catalogue
      .filter((m) => m.soldAs === size && m.series !== 'FTHT')
      .filter((m) => {
        const full = (series[m.series] && series[m.series].heat && series[m.series].heat.full) || 43;
        return hours.every((d) => HL.capacityTR(m.kw, full, d.outC) * HL.KW_PER_TR >= d.needKw - 1e-9);
      })
      .sort((a, b) => a.kw - b.kw);
    if (holds.length) return { size, model: holds[0] };
  }
  return null;
}

const listOf = (xs) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
const pctLess = (a, b) => (a > 0 ? Math.round(((a - b) / a) * 100) : 0);

// The verdict in words. focus 'inverter' (guide 1), 'units' (guide 2) or
// 'stars' (guide 4: the 3 Star against the 5 Star).
// Returns { tone: 'good' | 'warn', text } with **bold**.
export function roomVerdict(cmp, focus = 'inverter', bigger = null, setC = 24) {
  const n = cmp.hours.length;
  const F = cmp.rows.find((r) => !r.ac.model.inverter);
  const T = cmp.rows.find((r) => r.ac.type === 'three');
  const V = cmp.rows.find((r) => r.ac.type === 'five');
  const anyFlat = cmp.rows.some((r) => r.flatHours > 0);
  const flatText = cmp.rows.map((r, i) => (i === 0
    ? `the ${r.ac.code} can't keep up for ${r.flatHours ? `${r.flatHours} of your ${n} hours` : 'none of your hours'}`
    : `the ${r.ac.code} for ${r.flatHours || 'none'}`));
  const big = bigger ? ` A ${Number(bigger.size)} ton AC would hold ${setC}°C through all of them.` : '';

  if (focus === 'units') {
    const used = cmp.rows.map((r, i) => (i === 0
      ? `the ${r.ac.code} uses **${r.units.toFixed(1)} units**`
      : `the ${r.ac.code} **${r.units.toFixed(1)}**`));
    const label = cmp.rows.map((r) => `${((r.ac.model.annualKwh / 1600) * n).toFixed(1)}`);
    let text = `In your ${n} hours ${listOf(used)}. Their BEE label averages would say ${listOf(label)} units.`;
    if (anyFlat) text += ` Here ${listOf(flatText)}: at full power, an AC uses the most electricity it can, every minute of the hour.${big}`;
    return { tone: anyFlat ? 'warn' : 'good', text };
  }

  if (focus === 'stars') {
    if (!T || !V) return { tone: 'good', text: '' };
    const save = T.units - V.units;
    let text = `In your ${n} hours the ${T.ac.code} 3 Star uses **${T.units.toFixed(1)} units** and the ${V.ac.code} 5 Star **${V.units.toFixed(1)}**: the 5 Star saves **${save.toFixed(1)} units (${pctLess(T.units, V.units)}%)**, about ₹${Math.round(save * 30 * 8 * 8).toLocaleString('en-IN')} over 8 months of days like this at ₹8 a unit.`;
    if (T.flatHours > V.flatHours) text += ` The ${T.ac.code} can't keep up for ${T.flatHours} of your hours${V.flatHours ? ` and the ${V.ac.code} ${V.flatHours}` : `; the ${V.ac.code}, rated to give full cooling up to ${V.ac.full}°C, holds ${setC}°C`}.${big}`;
    return { tone: T.flatHours > V.flatHours || save > 0 ? 'good' : 'warn', text };
  }

  if (!F || (!T && !V)) return { tone: 'good', text: '' };
  if (!anyFlat) {
    const parts = [T, V].filter(Boolean).map((r, i) => (i === 0
      ? `the ${r.ac.code} uses **${r.units.toFixed(1)} units (${pctLess(F.units, r.units)}% less)**`
      : `the ${r.ac.code} **${r.units.toFixed(1)} (${pctLess(F.units, r.units)}% less)**`));
    return {
      tone: 'good',
      text: `**The AC keeps up in all ${n} of your hours,** so an inverter turns down instead of switching on and off. Against the ${F.ac.code}'s ${F.units.toFixed(1)} units, ${listOf(parts)}.`,
    };
  }
  let text = `**Can't keep up for part of your day:** ${listOf(flatText)}. At full power, an inverter has nothing to turn down.`;
  if (T && T.units > F.units) {
    text += ` At full power the ${T.ac.code} uses more electricity than the ${F.ac.code}, so it ends up using **${T.units.toFixed(1)} units to the ${F.ac.code}'s ${F.units.toFixed(1)}**.`;
  } else if (T) {
    text += ` The ${T.ac.code} saves ${(F.units - T.units).toFixed(1)} units (${pctLess(F.units, T.units)}%).`;
  }
  if (V) {
    text += V.units < F.units
      ? ` The ${V.ac.code}${V.flatHours < F.flatHours ? `, rated to give full cooling up to ${V.ac.full}°C,` : ''} saves ${(F.units - V.units).toFixed(1)} units (${pctLess(F.units, V.units)}%).`
      : ` The ${V.ac.code} saves nothing here.`;
  }
  return { tone: 'warn', text: text + big };
}
